import { errorHandler } from 'src/middlewares/Auth/index.utilities';
import { useState, ChangeEvent, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LoginContainer, LoginInfo, LoginForm } from 'src/views/landingPage/Login/login.styles';
import {
  signInUser,
  updateApplicant,
  updateInvestor,
  signOutAll,
  setLoadingState
} from 'src/store/User/user.slice';
import { useNavigate } from 'react-router-dom';
import { ComposedModal } from 'src/components/shared/compositionComponents/ComposedModal/ComposedModal';

//Functions
import getIp from 'src/functions/getIp';
import getCurrentDate from 'src/functions/getCurrentDate';
import ModalError from 'src/components/shared/modals/Error/Error';
import { ErrorObject, SignProps } from 'src/store/User/user.interface';
import { IpAddress } from 'src/types/investorAndApplicant';
import { useAppThunkDispatch } from 'src/store/store';
import { useGeolocation } from 'src/hooks/useGeolocation';
import { useHandleLoginRedirect } from 'src/hooks/useHandleLoginRedirect';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppThunkDispatch();
  const location = useLocation();
  const [data, setData] = useState<SignProps>({
    email: '',
    password: ''
  });
  const [userNotConfirmed] = useState(false);
  const [userNotFound] = useState(false);
  //Modals
  const { getUserLocation, error: errorGeolocation } = useGeolocation();
  const [geolocationNotSupportedError, setGeolocationNotSupportedError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { handleRedirectFromLogin } = useHandleLoginRedirect();

  const modalErrorMessages = {
    geolocationNotSupportedError:
      'Lamentamos informarte que tu navegador actual no soporta la geolocalización. Por favor, prueba con un navegador diferente o actualiza tu versión actual.',
    generic:
      'Es posible que este error se deba a que has rechazado los permisos para acceder a tu geolocalización. Por favor, verifica tus ajustes de privacidad y asegúrate de que has permitido el acceso a la geolocalización en tu navegador.'
  };

  const handleData = (e: ChangeEvent<HTMLInputElement>) => {
    setData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  };

  useEffect(() => {
    if (errorGeolocation === 'Not supported') {
      setGeolocationNotSupportedError(true);
    }
  }, [errorGeolocation]);

  const handleSignInAction = async () => {
    try {
      await dispatch(signOutAll());
    } catch (error) {
      console.error('error signInOutAll', error);
    }
    await sendDataToServer();
  };

  const sendDataToServer = async () => {
    const { email, password } = data;
    try {
      dispatch(setLoadingState(true));
      const [userGeolocation, ip, date] = await Promise.all([
        getUserLocation(),
        getIp(),
        getCurrentDate()
      ]);
      dispatch(setLoadingState(false));
      if (!userGeolocation) return setShowModal(true);
      let response;
      try {
        response = await dispatch(signInUser({ email, password })).unwrap();
      } catch (error) {
        return;
      }
      if (response === undefined) throw new Error('User does not exist.');
      if (response?.trully?.label === 'Review' || response?.trully?.label === 'Threat') {
        navigate('/validation-page');
        return;
      }
      const geolocationResponse = response.geolocation;
      const input = {
        id: response.id as string,
        geolocation: geolocationResponse
          ? [...response.geolocation, userGeolocation]
          : [userGeolocation],
        ipAddress: [
          ...response.ipAddress,
          {
            IPv4: ip.IPv4,
            country_code: ip.country_code,
            country_name: ip.country_name,
            city: ip.city,
            state: ip.state,
            date: date
          }
        ] as IpAddress[]
      };
      setLoadingState(true);
      if (response?.groupName === 'applicant') {
        await dispatch(updateApplicant(input));
        handleRedirectFromLogin(
          { typeOfPerson: response.typeOfPerson, typeOfUser: 'applicant' },
          response.onboarding?.currentStep,
          response.onboarding?.complete
        );
      } else if (response?.groupName === 'investor') {
        await dispatch(updateInvestor(input));
        handleRedirectFromLogin(
          { typeOfPerson: response.typeOfPerson, typeOfUser: 'investor' },
          response.onboarding?.currentStep,
          response.onboarding?.complete
        );
      }
      setLoadingState(false);
    } catch (error: unknown) {
      if (isErrorObject(error)) {
        errorHandler(dispatch, error);
      } else {
        console.error('Error sign User:', error);
      }
      setLoadingState(false);
    }
  };

  function isErrorObject(error: unknown): error is ErrorObject {
    return typeof error === 'object' && error !== null && 'code' in error;
  }

  const sendToRegister = () => {
    navigate('/registro');
  };

  const sentToForgotPassword = () => {
    const user = location.pathname.split('/')[1];
    navigate(`/${user}/olvide-contraseña`);
  };

  return (
    <LoginContainer>
      <ModalError />
      <ComposedModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        messageTitle="Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo más tarde."
        message={
          geolocationNotSupportedError
            ? modalErrorMessages.geolocationNotSupportedError
            : errorGeolocation
            ? modalErrorMessages.generic
            : undefined
        }
        type="error"
      />
      <LoginInfo>
        <div>
          <h2>
            <span>Financiamiento</span>a tu medida.
          </h2>
        </div>
        <p>
          Unimos empresas que necesitan créditos con personas que desean invertir en ellas en un
          modelo innovador de fondeo colectivo.
        </p>
        <ul>
          <li>Pre-Aprobación en 5 minutos</li>
          <li>Trámite 100% en línea.</li>
          <li>Solicita hasta 14 millones de pesos.</li>
        </ul>
      </LoginInfo>
      <LoginForm>
        <div>
          <h3>Iniciar sesión</h3>
          <p>
            Estimado usuario. Para cumplir con las leyes Mexicanas y asegurar la funcionalidad de
            nuestra aplicación, necesitamos tu permiso para la geolocalización. Por favor, autoriza
            este acceso en tu navegador. ¡Gracias por tu cooperación!
          </p>
          <input type="email" name="email" placeholder="Correo electrónico" onChange={handleData} />
          <input type="password" name="password" placeholder="Contraseña" onChange={handleData} />
          <button onClick={sentToForgotPassword}>Olvidé mi contraseña</button>
          <button onClick={handleSignInAction}>Continuar</button>
          <div>
            <p>¿Eres nuevo en FinSphera?</p>
            <button onClick={sendToRegister}>Regístrate aquí</button>
          </div>
          <button onClick={() => navigate('/confirmar-cuenta')}>
            Reenviar confirmación de cuenta
          </button>
          {userNotConfirmed && <p>Este usuario ya confirmó su correo electrónico</p>}
          {userNotFound && (
            <p>
              Este correo no se ha registrado. Inicia el proceso de registro{' '}
              <button onClick={sendToRegister}>aquí</button>
            </p>
          )}
        </div>
      </LoginForm>
    </LoginContainer>
  );
};

export default Login;
